import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { useTRPC } from '#src/trpc.js';
import { CrumbPopover } from './crumb-popover.js';
import { usePopoverSearch } from './use-popover-search.js';

export function RevisionCrumb({ projectId, revisionId }: { projectId: string; revisionId: string }) {
	const trpc = useTRPC();
	const navigate = useNavigate();
	const { anchorEl, isOpen, search, setSearch, handleOpen, handleClose } = usePopoverSearch();

	const { data: listData, isPending } = useQuery(
		trpc.revision.list.queryOptions({ projectId }, { throwOnError: false }),
	);

	const currentRevision = listData?.revisions.find((r) => r.id === revisionId);
	const displayName = currentRevision?.displayName ?? revisionId.slice(0, 8);

	const handleSelect = (selectedRevisionId: string) => {
		handleClose();
		void navigate({
			to: '.',
			params: (prev) => ({ ...prev, revisionId: selectedRevisionId }),
		});
	};

	const filtered = (listData?.revisions ?? [])
		.filter((r) => (r.displayName ?? r.id).toLowerCase().includes(search.toLowerCase()))
		.toSorted((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

	return (
		<>
			<div className='flex items-center'>
				<span className='py-0.5 text-sm text-gray-a10'>{displayName}</span>
				<IconButton size='small' onClick={handleOpen} className='-mr-1.5 ml-0.5 p-0.5 text-gray-a9 hover:text-gray-a12'>
					<ArrowDropDownIcon className='text-base' />
				</IconButton>
			</div>

			<CrumbPopover
				anchorEl={anchorEl}
				open={isOpen}
				onClose={handleClose}
				heading='Revisions'
				search={search}
				onSearchChange={setSearch}
				isPending={isPending}
				emptyLabel='No revisions found'
				isEmpty={filtered.length === 0}
			>
				{filtered.map((r) => (
					<MenuItem key={r.id} selected={r.id === revisionId} onClick={() => handleSelect(r.id)}>
						{r.displayName ?? r.id.slice(0, 8)}
					</MenuItem>
				))}
			</CrumbPopover>
		</>
	);
}
